import { Invoice, InvoiceService, ListInvoicesFilter } from '@galaxy/billing';
import { DataSource } from '@angular/cdk/table';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap, finalize, take } from 'rxjs/operators';

const getRequestPageSize = (currentDataLength: number, pageIndex: number, pageSize: number) => {
  const startIndex = pageIndex * pageSize;
  const endIndex = startIndex + pageSize;
  return endIndex - currentDataLength;
};

/**
 * Data source for the ListInvoicesDataSource view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data.
 */
export class ListInvoicesDataSource extends DataSource<Invoice> {
  private storedInvoices$$: BehaviorSubject<Invoice[]> = new BehaviorSubject<Invoice[]>([]);
  private displayedInvoices$$: BehaviorSubject<Invoice[]> = new BehaviorSubject<Invoice[]>([]);
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private cursor$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private hasMore$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _storedInvoices$ = this.storedInvoices$$.asObservable();
  private _displayedInvoices$ = this.displayedInvoices$$.asObservable();
  private _loading$ = this.loading$$.asObservable();
  private _hasMore$ = this.hasMore$$.asObservable();

  constructor(private invoiceService: InvoiceService) {
    super();
  }

  get hasMore$(): Observable<boolean> {
    return this._hasMore$;
  }

  get loading$(): Observable<boolean> {
    return this._loading$;
  }

  get storedInvoices$(): Observable<Invoice[]> {
    return this._storedInvoices$;
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<Invoice[]> {
    return this._displayedInvoices$;
  }

  disconnect(): void {
    this.storedInvoices$$.complete();
    this.displayedInvoices$$.complete();
    this.loading$$.complete();
    this.cursor$$.complete();
    this.hasMore$$.complete();
  }

  public updatePage(filters: ListInvoicesFilter, pageSize: number, pageIndex: number): void {
    const storedItems = this.storedInvoices$$.getValue();
    const requestPageSize = getRequestPageSize(storedItems.length, pageIndex, pageSize);
    if (this.hasMore$$.getValue() && requestPageSize > 0) {
      this.load(filters, requestPageSize, pageIndex);
    } else {
      this.setPagedData(storedItems, pageIndex, pageSize);
    }
  }

  public load(filters: ListInvoicesFilter, pageSize: number, pageIndex: number): void {
    this.list(filters, this.cursor$$.getValue(), pageSize)
      .pipe(
        take(1),
        map((newItems) => [...this.storedInvoices$$.value, ...newItems]),
      )
      .subscribe((items) => {
        this.storedInvoices$$.next(items);
        this.setPagedData(items, pageIndex, pageSize);
      });
  }

  public reload(filters: ListInvoicesFilter, pageSize: number, pageIndex: number): void {
    this.list(filters, '', pageSize)
      .pipe(take(1))
      .subscribe((items) => {
        this.storedInvoices$$.next(items);
        this.setPagedData(items, pageIndex, pageSize);
      });
  }

  private setPagedData(items: Invoice[], pageIndex: number, pageSize: number): void {
    // return the whole page, or whatever is left of our merchant discount list
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const endIndexForExisting = Math.min(items.length, endIndex);
    this.displayedInvoices$$.next(items.slice(startIndex, endIndexForExisting));
  }

  private list(filters: ListInvoicesFilter, cursor: string, pageSize: number): Observable<Invoice[]> {
    this.loading$$.next(true);
    return this.invoiceService.list(filters, cursor, pageSize).pipe(
      tap((r) => {
        this.hasMore$$.next(r.hasMore);
        this.cursor$$.next(r.nextCursor);
      }),
      map((r) => r.results),
      finalize(() => this.loading$$.next(false)),
    );
  }
}
